import { Button, Icon } from '@pypestream/design-system';
import { observer } from 'mobx-react-lite';
import { FC } from 'react';

import { ConfigPanel } from '../components/table/src/config-panel';
import { useProStudioViewerRole } from '../hooks';
import { tableStore } from '../stores/table-store';
import {
  type HealthIssue,
  type HealthSeverity,
} from '../stores/utils/solution-health';
import { useTopUnderBuildBar } from './build-panel-position';
import { useSolutionHealth } from './use-solution-health';

import './build-health-panel.scss';

const SEVERITY_LABEL: Record<HealthSeverity, string> = {
  error: 'Needs fixing',
  warning: 'Worth a look',
  info: 'For your information',
};

const IssueRow: FC<{ issue: HealthIssue; canFix: boolean }> = ({
  issue,
  canFix,
}) => (
  <li
    className={`c-build-health__issue c-build-health__issue--${issue.severity}`}
  >
    <span className="c-build-health__dot" aria-hidden="true" />
    <div className="c-build-health__body">
      <span className="c-build-health__title">{issue.title}</span>
      <span className="c-build-health__detail">{issue.detail}</span>
      <div className="c-build-health__actions">
        {issue.rows.slice(0, 6).map((r) => (
          <button
            key={r.rowId}
            type="button"
            className="c-build-health__node"
            title={r.name ? `Open #${r.nodeNumber} ${r.name}` : undefined}
            onClick={() => tableStore.openNodeInBuild(r.rowId)}
          >
            #{r.nodeNumber}
          </button>
        ))}
        {issue.rows.length > 6 && (
          <span className="c-build-health__more">
            +{issue.rows.length - 6} more
          </span>
        )}
        {issue.fix && canFix && (
          <Button
            size="small"
            variant="secondary"
            test-id={`bobcat-health-fix-${issue.rule}`}
            onClick={() => {
              const rowId = tableStore.applyHealthFix(issue.fix!);
              if (rowId && issue.fix?.kind === 'add-error-handler') {
                tableStore.openNodeInBuild(rowId);
              }
            }}
          >
            {issue.fix.kind === 'add-error-handler'
              ? 'Add error handler'
              : `Route errors to #${issue.fix.errorTarget}`}
          </Button>
        )}
      </div>
    </div>
  </li>
);

export const BuildHealthPanel: FC = observer(() => {
  const isViewer = useProStudioViewerRole();
  const top = useTopUnderBuildBar();
  const issues = useSolutionHealth();
  const gate = tableStore.healthPublishGate;

  const groups = (['error', 'warning', 'info'] as HealthSeverity[])
    .map((severity) => ({
      severity,
      items: issues.filter((i) => i.severity === severity),
    }))
    .filter((g) => g.items.length);

  return (
    <div
      className="c-build-actions c-build-health"
      style={{ top }}
      data-testid="bobcat-build-health-panel"
    >
      <ConfigPanel
        title="Health"
        onClose={() => {
          tableStore.setHealthPanelOpen(false);
          tableStore.setHealthPublishGate(false);
        }}
      >
        {gate && (
          <div className="c-build-health__gate" role="alert">
            <strong>
              {issues.filter((i) => i.severity === 'error').length} problem
              {issues.filter((i) => i.severity === 'error').length === 1
                ? ''
                : 's'}{' '}
              to fix before publishing.
            </strong>
            <span>Fix them below, or publish anyway.</span>
            <Button
              size="small"
              variant="secondary"
              test-id="bobcat-health-publish-anyway"
              onClick={() => {
                tableStore.setHealthPublishGate(false);
                tableStore.requestPublishAnyway();
              }}
            >
              Publish anyway
            </Button>
          </div>
        )}

        {issues.length === 0 ? (
          <p className="c-build-health__clear">
            <Icon name="check" size="small" /> Nothing to fix — this solution
            passes every check.
          </p>
        ) : (
          groups.map((g) => (
            <section key={g.severity}>
              <span className="c-build-actions__group">
                {SEVERITY_LABEL[g.severity]} · {g.items.length}
              </span>
              <ul className="c-build-health__list">
                {g.items.map((issue) => (
                  <IssueRow key={issue.id} issue={issue} canFix={!isViewer} />
                ))}
              </ul>
            </section>
          ))
        )}
      </ConfigPanel>
    </div>
  );
});
